import {
  Alert,
  ActionIcon,
  Anchor,
  Button,
  Checkbox,
  Chip,
  CloseButton,
  Combobox,
  Fieldset,
  Indicator,
  Input,
  List,
  Modal,
  MultiSelect,
  NavLink,
  NumberInput,
  Pagination,
  Paper,
  PillsInput,
  Select,
  Switch,
  TagsInput,
  UnstyledButton,
  Radio,
  Textarea,
  createTheme,
  defaultVariantColorsResolver,
  parseThemeColor,
  rgba,
  PasswordInput,
  darken,
  lighten,
  FileInput,
  Container,
  ThemeIcon
} from '@mantine/core'
import { DateInput, DatePicker } from '@mantine/dates'
import { RichTextEditor } from '@mantine/tiptap'
import * as actionIconStyles from './modules/ActionIcon.module.scss'
import * as anchorStyles from './modules/Anchor.module.scss'
import * as buttonStyles from './modules/Button.module.scss'
import * as checkboxStyles from './modules/Checkbox.module.scss'
import * as dateInputStyles from './modules/DateInput.module.scss'
import * as fieldsetStyles from './modules/Fieldset.module.scss'
import * as indicatorStyles from './modules/Indicator.module.scss'
import * as inputStyles from './modules/Input.module.scss'
import * as listStyles from './modules/List.module.scss'
import * as numberInputStyles from './modules/NumberInput.module.scss'
import * as paginationStyles from './modules/Pagination.module.scss'
import * as paperStyles from './modules/Paper.module.scss'
import * as richTextEditorStyles from './modules/RichTextEditor.module.scss'
import * as selectStyles from './modules/Select.module.scss'
import * as tagsInputStyles from './modules/TagsInput.module.scss'
import * as pillsInputStyles from './modules/PillsInput.module.scss'
import * as comboboxStyles from './modules/Combobox.module.scss'
import * as radioStyles from './modules/Radio.module.scss'
import * as textareaStyles from './modules/Textarea.module.scss'
import * as modalStyles from './modules/Modal.module.scss'
import * as alertStyles from './modules/Alert.module.scss'
import * as navLinkStyles from './modules/NavLink.module.scss'
import * as passwordInputStyles from './modules/PasswordInput.module.scss'
import * as responsiveContainerStyles from './modules/ResponsiveContainer.module.scss'
import * as themeIconStyles from './modules/ThemeIcon.module.scss'

const blueShades = Object.freeze([
  '#F8FCFF',
  '#CFEDFF',
  '#A8DFFF',
  '#86D2FF',
  '#65C6FF',
  '#48BBFF',
  '#2DB1FF', // Hirescore Color
  '#008EE2',
  '#0076BB',
  '#006BAA'
])

export const theme = createTheme({
  spacing: {
    xxs: '0.425rem',
    xxxs: '0.125rem',
    xxl: '2.5rem'
  },
  colors: {
    blue: blueShades,
    info: blueShades,
    success: [
      '#E6FCF5',
      '#C3FAE8',
      '#96F2D7',
      '#63E6BE',
      '#38D9A9',
      '#20C997',
      '#12B886',
      '#0CA678',
      '#099268',
      '#087F5B'
    ],
    error: [
      '#FFF5F5',
      '#FFE3E3',
      '#FFC9C9',
      '#FFA8A8',
      '#FF8787',
      '#FF6B6B',
      '#FA5252',
      '#F03E3E',
      '#E03131',
      '#C92A2A'
    ],
    warning: [
      '#FFF9DB',
      '#FFF3BF',
      '#FFEC99',
      '#FFE066',
      '#FFD43B',
      '#FCC419',
      '#FAB005',
      '#F59F00',
      '#F08C00',
      '#E67700'
    ]
  },
  components: {
    Button: Button.extend({ classNames: buttonStyles }),
    NavLink: NavLink.extend({ classNames: navLinkStyles, defaultProps: { color: 'dark', variant: 'subtle' } }),
    ActionIcon: ActionIcon.extend({ classNames: actionIconStyles, defaultProps: { color: 'gray', variant: 'subtle' } }),
    Input: Input.extend({ classNames: inputStyles }),
    FileInput: FileInput.extend({
      styles: {
        input: {
          marginBottom: 0
        }
      }
    }),
    PasswordInput: PasswordInput.extend({ classNames: passwordInputStyles }),
    CloseButton: CloseButton.extend({ classNames: buttonStyles }),
    UnstyledButton: UnstyledButton.extend({ classNames: buttonStyles }),
    Pagination: Pagination.extend({ classNames: paginationStyles }),
    TagsInput: TagsInput.extend({ classNames: tagsInputStyles }),
    DateInput: DateInput.extend({ classNames: dateInputStyles }),
    DatePicker: DatePicker.extend({ classNames: dateInputStyles }),
    Paper: Paper.extend({ classNames: paperStyles }),
    Modal: Modal.extend({
      classNames: modalStyles,
      styles: {
        content: { padding: 0 }
      }
    }),
    MultiSelect: MultiSelect.extend({ classNames: [tagsInputStyles, selectStyles] }),
    Select: Select.extend({ classNames: selectStyles }),
    Combobox: Combobox.extend({ classNames: comboboxStyles }),
    Checkbox: Checkbox.extend({ classNames: checkboxStyles }),
    Chip: Chip.extend({
      styles: {
        label: { display: 'inline-flex', verticalAlign: 'middle', alignItems: 'center', alignContent: 'center' }
      }
    }),
    List: List.extend({ classNames: listStyles }),
    Fieldset: Fieldset.extend({ classNames: fieldsetStyles }),
    Anchor: Anchor.extend({ classNames: anchorStyles }),
    NumberInput: NumberInput.extend({ classNames: numberInputStyles }),
    RichTextEditor: RichTextEditor.extend({ classNames: richTextEditorStyles }),
    Indicator: Indicator.extend({ classNames: indicatorStyles }),
    Switch: Switch.extend({
      classNames: inputStyles,
      styles: {
        thumb: {
          top: '0.125rem'
        }
      }
    }),
    PillsInput: PillsInput.extend({ classNames: pillsInputStyles }),
    Radio: Radio.extend({
      classNames: radioStyles,
      vars: (theme, props) => {
        if (props.size === 'xl') {
          return {
            root: {
              '--radio-icon-size': 'var(--radio-icon-size-xl)'
            }
          }
        }
        if (props.size === 'lg') {
          return {
            root: {
              '--radio-icon-size': 'var(--radio-icon-size-lg)'
            }
          }
        }
        if (props.size === 'md') {
          return {
            root: {
              '--radio-icon-size': 'var(--radio-icon-size-md)'
            }
          }
        }
        if (props.size === 'sm') {
          return {
            root: {
              '--radio-icon-size': 'var(--radio-icon-size-sm)'
            }
          }
        }
        if (props.size === 'xs') {
          return {
            root: {
              '--radio-icon-size': 'var(--radio-icon-size-xs)'
            }
          }
        }
        return { root: {} }
      }
    }),
    Alert: Alert.extend({
      classNames: alertStyles
    }),
    Textarea: Textarea.extend({
      classNames: textareaStyles
    }),
    Container: Container.extend({
      classNames: responsiveContainerStyles
    }),
    ThemeIcon: ThemeIcon.extend({
      classNames: themeIconStyles
    })
  },
  variantColorResolver: (input) => {
    const defaultResolvedColors = defaultVariantColorsResolver(input);
    const parsedColor = parseThemeColor({
      color: input.color || input.theme.primaryColor,
      theme: input.theme
    });

    if (input.variant === 'paper') {
      const paperBackground = `var(--mantine-color-${parsedColor.color}-1)`;

      return {
        background: paperBackground,
        border: 'none',
        color: parsedColor.value
      }
    }

    if (input.variant === 'light') {
      return {
        ...defaultResolvedColors,
        hover: rgba(parsedColor.value, 0.2),
        hoverColor: `var(--mantine-color-${parsedColor.color}-light-color)`
      }
    }

    if (input.variant === 'discreet') {
      return {
        background: 'inherit',
        color: 'inherit',
        hover: `var(--mantine-color-${parsedColor.color}-light)`,
        hoverColor: `var(--mantine-color-${parsedColor.color}-light-color)`
      }
    }

    if (input.variant === 'subtle') {
      return {
        ...defaultResolvedColors,
        background: 'transparent',
        color: parsedColor.value,
        hover: rgba(parsedColor.value, 0.1)
      }
    }

    // active styles remove transparency and highlight elements with parsed color
    if (input.variant.startsWith('active-')) {
      const baseVariant = input.variant.split('-')[1]
      switch (baseVariant) {
        case 'filled':
          return {
            background: `var(--mantine-color-${parsedColor.color}-7)`,
            hover: `var(--mantine-color-${parsedColor.color}-8)`
          }
        case 'outline':
          return {
            background: `var(--mantine-color-${parsedColor.color}-0)`,
            hover: `var(--mantine-color-${parsedColor.color}-1)`,
            color: `var(--mantine-color-${parsedColor.color}-6)`,
            border: `1px solid var(--mantine-color-${parsedColor.color}-6)`
          }
        default:
          if (parsedColor.isThemeColor) {
            return {
              background: darken(`var(--mantine-color-${parsedColor.color}-1)`, 0.1),
              hover: darken(`var(--mantine-color-${parsedColor.color}-2)`, 0.1),
              color: `var(--mantine-color-${parsedColor.color}-6)`
            }
          } else {
            return {
              background: lighten(parsedColor.value, 0.7),
              hover: lighten(parsedColor.value, 0.6),
              color: parsedColor.value
            }
          }
      }
    }

    if (input.variant === 'danger-subtle') {
      return {
        background: 'transparent',
        color: parsedColor.value,
        hover: 'var(--mantine-color-red-light-hover)'
      }
    }

    return defaultResolvedColors
  }
})
